import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit
} from '@angular/core';
import { SubscriberAdvertiserModule } from '../../subscriber-advertiser.module';
import { BaseSubscriberAdvertiserComponent } from '../../abstract-components/base-subscriber-advertiser.component';
import { GlobalFacade } from '~app/app/~store/facades/global.facade';
import { Advertiser } from '~core/models/advertiser.models';
import { LoadListener } from '~app/app/~shared/utils/rxjs';
import { Brand } from '~core/models/brand.models';
import { ProfileService } from '~app/app/~shared/services/profile.service';
import { map } from 'rxjs';
import { SubscriberMetricsService } from '~app/app/~shared/services/subscriber-metrics.service';
import { ChartOptions } from '~app/app/~shared/models/shared.models';
import {
  calculateAdsExpensePercentage,
  fetchBrandedStats,
  generateAdsExpensesBarChartOptions,
  generateAdsPerPeriodLineChartOptions,
  generateAdsPerTimeSlotChartOptions
} from '~app/app/~shared/utils/chart-options';
import {
  AdvertisingExpenses,
  AdvertisingPerPeriod,
  AdvertisingPerTimeSLot
} from '~core/models/metrics.models';
import { MEDIA_TYPES } from '~app/app/~shared/constants/global.constants';
import { NgbProgressbarModule } from '@ng-bootstrap/ng-bootstrap';
import { AdsExpensesChartComponent } from '../../components/ads-expenses-chart/ads-expenses-chart.component';
import { AdsSlotsChartComponent } from '../../components/ads-slots-chart/ads-slots-chart.component';
import { UsedMediasChartComponent } from '../../components/used-medias-chart/used-medias-chart.component';
import { MediasExpensesChartComponent } from '../../components/medias-expenses-chart/medias-expenses-chart.component';

@Component({
  selector: 'subscriber-advertiser-details-page',
  standalone: true,
  imports: [
    SubscriberAdvertiserModule,
    NgbProgressbarModule,
    AdsExpensesChartComponent,
    AdsSlotsChartComponent,
    UsedMediasChartComponent,
    MediasExpensesChartComponent
  ],
  templateUrl: './subscriber-advertiser-details-page.component.html',
  styleUrl: './subscriber-advertiser-details-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SubscriberAdvertiserDetailsPageComponent
  extends BaseSubscriberAdvertiserComponent
  implements OnInit
{
  protected _globalFacade = inject(GlobalFacade);
  protected _profileService = inject(ProfileService);
  protected _subscriberMetricsService = inject(SubscriberMetricsService);
  advertiser: Advertiser | undefined;

  brandsListener: LoadListener<Brand[]> | undefined;
  adsExpensesListener: LoadListener<ChartOptions> | undefined;
  adsSlotsListener: LoadListener<ChartOptions> | undefined;
  usedMediasListener: LoadListener<ChartOptions> | undefined;
  mediaExpensesPercentListener: LoadListener<AdvertisingExpenses[]> | undefined;

  globalBrand: Brand | undefined;
  expensesBrandFilters: Array<Brand> = [];
  slotsBrandFilters: Array<Brand> = [];
  mediasBrandFilters: Array<Brand> = [];

  mediaTypes = MEDIA_TYPES;

  ngOnInit(): void {
    this.advertiser = this._globalFacade.getRouteData()!.state as Advertiser;
    this.initBrandListener();
    this.initExpensesChart();
    this.initSlotsChart();
    this.initUsedMediasChart();
    this.initMediaExpensesPercentage();
  }

  initBrandListener(): void {
    const brands$ = this._profileService
      .listBrands({
        organizationId: this.advertiser?.id
      })
      .pipe(map(brands => brands.items));
    this.brandsListener = new LoadListener(brands$);
  }

  initExpensesChart(): void {
    const brandFilters = this.expensesBrandFilters;
    const { id, name } = this.advertiser ?? {};
    const adsExpenses$ = fetchBrandedStats<AdvertisingExpenses[]>(
      params => this._subscriberMetricsService.getAdvertisingExpenses(params),
      brandFilters,
      id,
      name
    ).pipe(map(generateAdsExpensesBarChartOptions));

    this.adsExpensesListener = new LoadListener(adsExpenses$);
  }

  initSlotsChart(): void {
    const brandFilters = this.slotsBrandFilters;
    const { id, name } = this.advertiser ?? {};

    const adsSlots$ = fetchBrandedStats<AdvertisingPerTimeSLot[]>(
      params =>
        this._subscriberMetricsService.getAdvertisingPerTimeSlot(params),
      brandFilters,
      id,
      name
    ).pipe(map(generateAdsPerTimeSlotChartOptions));

    this.adsSlotsListener = new LoadListener(adsSlots$);
  }

  initUsedMediasChart(additionalParams?: Record<string, unknown>): void {
    const brandFilters = this.mediasBrandFilters;
    const { id, name } = this.advertiser ?? {};

    const usedMedia$ = fetchBrandedStats<AdvertisingPerPeriod[]>(
      params => this._subscriberMetricsService.getAdvertisingPerPeriod(params),
      brandFilters,
      id,
      name,
      additionalParams
    ).pipe(map(generateAdsPerPeriodLineChartOptions));

    this.usedMediasListener = new LoadListener(usedMedia$);
  }

  updateAllChartData(): void {
    if (this.globalBrand) {
      this.syncGlobalToChartBrand();
    } else {
      this.expensesBrandFilters = [];
      this.slotsBrandFilters = [];
      this.mediasBrandFilters = [];
    }

    this.initExpensesChart();
    this.initSlotsChart();
    this.initUsedMediasChart();
    this.initMediaExpensesPercentage(this.globalBrand?.id);
  }

  initMediaExpensesPercentage(brandId?: string): void {
    const { id } = this.advertiser ?? {};

    const expensesPercentage$ = brandId
      ? this._subscriberMetricsService.getAdvertisingExpenses({ brandId })
      : this._subscriberMetricsService.getAdvertisingExpenses({
          organizationId: id
        });

    this.mediaExpensesPercentListener = new LoadListener(
      expensesPercentage$.pipe(map(calculateAdsExpensePercentage))
    );
  }

  syncGlobalToChartBrand(): void {
    if (this.globalBrand) {
      this.expensesBrandFilters[0] = this.globalBrand;
      this.slotsBrandFilters[0] = this.globalBrand;
      this.mediasBrandFilters[0] = this.globalBrand;
      this._cdRef.detectChanges();
    }
  }
}
